<template>
    <div class="action-user-create">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h2 class="mb-0">{{ $t('action_user.create_title') || 'Create Action User' }}</h2>
        </div>

        <!-- Customer Selection -->
        <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ $t('action_user.select_customers') || 'Select Customers' }}</h5>
                <div class="d-flex gap-2">
                    <button @click="selectAllCustomers" class="btn btn-sm btn-outline-primary">
                        {{ $t('common.select_all') || 'Select All' }}
                    </button>
                    <button @click="deselectAllCustomers" class="btn btn-sm btn-outline-secondary">
                        {{ $t('common.deselect_all') || 'Deselect All' }}
                    </button>
                </div>
            </div>
            <div class="card-body">
                <!-- Search and Filter -->
                <div class="row mb-3">
                    <div class="col-md-6">
                        <input v-model="searchQuery" @input="handleSearch" type="text" class="form-control"
                            :placeholder="$t('common.search') || 'Search customers...'">
                    </div>
                </div>

                <!-- CrudTable for Customers/Integration Users -->
                <CrudTable :columns="columns" :shouldShowSearchBox="false" :dataSources="customers" :loader="isLoading"
                    listUrl="admin/active-customer" @page-change="changePage" @per-page-change="changePerPage">
                    <template #head(select)>
                        <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll"
                            class="form-check-input" />
                    </template>
                    <template #cell(select)="row">
                        <input type="checkbox" :checked="isSelected(row.item)" @change="toggleCustomer(row.item)"
                            class="form-check-input" />
                    </template>
                </CrudTable>

                <!-- Pagination Info -->
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        <span class="text-muted">
                            {{ $t('common.showing') || 'Showing' }} {{ paginationInfo.from }} - {{ paginationInfo.to }}
                            {{ $t('common.of') || 'of' }} {{ paginationInfo.total }} {{ $t('common.entries') ||
                                'entries' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Submit Button -->
        <div class="d-flex justify-content-end gap-2">
            <button @click="goBack" class="btn btn-secondary">
                {{ $t('common.cancel') || 'Cancel' }}
            </button>
            <button @click="submitForm" :disabled="!canSubmit || isSubmitting" class="btn btn-primary">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ $t('action_user.assign') || 'Create Action User' }}
            </button>
        </div>
    </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from '@/components/CrudTable.vue';

export default {
    name: 'ActionUserCreate',
    components: { CrudTable },
    data() {
        return {
            isLoading: false,
            isSubmitting: false,
            currentPage: 1,
            perPage: 10,
            searchQuery: '',
            statusFilter: '',
            customers: [],
            selectedCustomers: [],
            paginationInfo: {
                from: 0,
                to: 0,
                total: 0
            },
            columns: [
                { key: 'select', label: '', thStyle: { width: '50px' } },
                { key: 'full_name', label: this.$t('customer.table_header_customer') || 'Customer Name' },
                { key: 'phone', label: this.$t('customer.table_header_phone') || 'Phone' },
                { key: 'email', label: this.$t('customer.table_header_email') || 'Email' },
                // { key: 'nationality', label: this.$t('customer.table_header_nationality') || 'Nationality' },
                // { key: 'is_active', label: this.$t('customer.table_header_status') || 'Status' }
            ]
        }
    },
    computed: {
        isAllSelected() {
            return this.customers.length > 0 &&
                this.customers.every(customer => this.isSelected(customer));
        },
        canSubmit() {
            return this.selectedCustomers.length > 0;
        }
    },
    async mounted() {
        try {
            await this.checkIfManualAction();
            await this.fetchCustomers();
        } catch (e) {
            console.error('Error in mounted:', e);
        }
    },
    methods: {
        getServiceLogo(service) {
            const logos = {
                "G-suit": require("@/assets/google.png"),
                "Salesforce": require("@/assets/salesforce.png"),
                "ZOHO": require("@/assets/zoho.png"),
                "Monday": require("@/assets/monday.png"),
                "Microsoft 365": require("@/assets/office365.png"),
                "SAP": require('@/assets/sap.png'),
            };
            return logos[service] || require("@/assets/integration.png");
        },
        isSelected(customer) {
            return this.selectedCustomers.some(
                id => id === customer.id
            );
        },
        toggleCustomer(customer) {
            const idx = this.selectedCustomers.findIndex(
                id => id === customer.id
            );
            if (idx > -1) {
                this.selectedCustomers.splice(idx, 1);
            } else {
                this.selectedCustomers.push(customer.id);
            }
        },
        selectAllCustomers() {
            this.customers.forEach(customer => {
                if (!this.isSelected(customer)) {
                    this.toggleCustomer(customer)
                }
            });
        },
        deselectAllCustomers() {
            this.customers.forEach(customer => {
                if (this.isSelected(customer)) {
                    this.toggleCustomer(customer)
                }
            });
        },
        toggleSelectAll() {
            if (this.isAllSelected) {
                this.deselectAllCustomers();
            } else {
                this.selectAllCustomers();
            }
        },
        async fetchCustomers() {
            try {
                this.isLoading = true;
                const response = await request({
                    method: "get",
                    url: "admin/active-customer",
                    params: {
                        page: this.currentPage,
                        per_page: this.perPage,
                        sort_by: 'created_at',
                        sort_direction: 'desc',
                        search: this.searchQuery,
                        is_active: this.statusFilter || undefined
                    }
                });
                this.customers = response.data || [];
                this.paginationInfo = {
                    from: response.meta?.from || 0,
                    to: response.meta?.to || 0,
                    total: response.meta?.total || 0
                };
                await this.fetchSelectedEntities();
            } catch (error) {
                console.error('Error fetching customers:', error);
                this.customers = [];
                this.paginationInfo = { from: 0, to: 0, total: 0 };
            } finally {
                this.isLoading = false;
            }
        },
        handleSearch() {
            this.currentPage = 1;
            this.fetchCustomers();
        },
        handleFilter() {
            this.currentPage = 1;
            this.fetchCustomers();
        },
        changePage(page) {
            this.currentPage = page;
            this.fetchCustomers();
        },
        changePerPage(perPage) {
            this.perPage = perPage;
            this.currentPage = 1;
            this.fetchCustomers();
        },
        async submitForm() {
                if (!this.canSubmit) return;
                try {
                    this.isSubmitting = true;
                    const actionId = this.$route.params.id;
                    await request({
                        method: "post",
                        url: "action-users",
                        data: {
                            action_id: actionId,
                            customers: this.selectedCustomers
                        }
                    });
                    this.$bvToast.toast(this.$t('action_user.created_successfully'), {
                        title: 'Status Updated',
                        variant: 'success'
                    });
                    this.goBack();
                } catch (error) {
                    console.error('Error creating action user:', error);
                } finally {
                    this.isSubmitting = false;
                }
            },
        async fetchSelectedEntities() {
            const actionId = this.$route.params.id;
            const response = await request({
                method: "get",
                url: `action-users/selected-entities/${actionId}`
            });
            this.selectedCustomers = response || [];
        },
        goBack() {
            this.$router.go(-1);
        },
        async checkIfManualAction(){
            const actionId = this.$route.params.id;
            const response = await request({
                method: "get",
                url: `action-users/check-if-manual-action/${actionId}`
            });
            if(!response.data){
                this.$router.replace(`/admin/actions`);
            }
        }
    }
}
</script>

<style scoped>
.customer-row:hover {
    background-color: #f8f9fa;
}

.form-check-input:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
}

::v-deep .b-table tbody tr:hover {
    background-color: #f0f4fa !important;
    transition: background 0.2s;
}

::v-deep .b-table th,
::v-deep .b-table td {
    vertical-align: middle !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #eaeaea !important;
}

::v-deep .b-table th {
    background: #f8fafc !important;
    font-weight: 600;
    color: #333;
    border-top: 1px solid #eaeaea !important;
}
</style>
